import { SettingCell } from '@/components/setting-cell'
import { cn, formatMoney, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'

type QuantityPricing = components['schemas']['StoreQuantityPricingRead']
type Charge = components['schemas']['QuantityPricingChargeRead']

/** "$10" for whole dollars, "$2.50" otherwise, as suppliers write their terms. */
function dollars(value: string): string {
  const amount = Number(value)
  return Number.isInteger(amount) ? `$${amount.toLocaleString('en-US')}` : formatMoney(value)
}

function chargeText(charge: Charge, casePack: number | null | undefined): string {
  const amount = dollars(charge.amount)
  switch (charge.kind) {
    case 'small_order_fee':
      return `${amount} fee on orders under ${dollars(charge.order_under ?? '0')}`
    case 'minimum_order':
      return `${amount} minimum order`
    case 'order_fee':
      return `${amount} ${charge.label.toLowerCase()} on orders under ${dollars(charge.order_under ?? '0')}`
    case 'item_fee':
      return `${amount} ${charge.label.toLowerCase()} per item`
    case 'broken_case_fee': {
      const pack = casePack ? ` of ${casePack}` : ''
      const cap = charge.max_per_order ? `, at most ${dollars(charge.max_per_order)} an order` : ''
      return `${amount} fee per broken case${pack}${cap}`
    }
    case 'broken_case_discount': {
      const pack = casePack ? ` of ${casePack}` : ''
      const chain = charge.discount ? `${charge.discount}, ` : ''
      return `smaller discount below a case${pack} (${chain}${formatMoney(charge.amount)} more per item)`
    }
    case 'dropship_fee':
      return `${amount} dropship fee per order`
    default:
      return `${amount} ${charge.label.toLowerCase()}`
  }
}

function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** The store's quantity steps: "1–32", "33–64", "65+". */
function steps(pricing: QuantityPricing): { label: string; price: string }[] {
  const breaks = pricing.price_breaks ?? []
  const all = [{ minimum_quantity: 1, unit_price: pricing.quantity_one_price }, ...breaks]
  return all.map((step, index) => {
    const next = all[index + 1]
    const label = !next
      ? `${step.minimum_quantity}+`
      : next.minimum_quantity - 1 === step.minimum_quantity
        ? `${step.minimum_quantity}`
        : `${step.minimum_quantity}–${next.minimum_quantity - 1}`
    return { label, price: step.unit_price }
  })
}

/** The bulk-pricing table the store shows, and the manufacturer charges it covers (USA-1549). */
export function BulkPricingDetail({ pricing }: { pricing: QuantityPricing | null | undefined }) {
  if (!pricing) return null
  const charges = (pricing.charges ?? []).map((charge) => chargeText(charge, pricing.case_pack))
  const rows = steps(pricing)
  return (
    <section aria-labelledby="bulk-pricing" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-baseline justify-between gap-3 border-b px-4 py-3">
        <h2 id="bulk-pricing" className="text-sm font-semibold">
          Bulk pricing
        </h2>
        {pricing.updated_at && (
          <p className="text-xs text-muted-foreground">
            Updated{' '}
            {parseUTCDate(pricing.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </p>
        )}
      </div>
      {charges.length > 0 && (
        <p className="border-b px-4 py-3 text-sm" data-testid="bulk-pricing-reason">
          Covers the manufacturer&apos;s {joinAnd(charges)}.
        </p>
      )}
      <div
        className={cn(
          '-mb-px -mr-px grid',
          rows.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
          rows.length > 3 && 'sm:grid-cols-4',
        )}
      >
        {rows.map((step) => (
          <SettingCell key={step.label} label={`Qty ${step.label}`}>
            <span className="whitespace-nowrap text-sm font-medium tabular-nums">
              {formatMoney(step.price)}
              <span className="hidden font-normal text-muted-foreground sm:inline"> each</span>
            </span>
          </SettingCell>
        ))}
      </div>
    </section>
  )
}
